import type { Word } from './types.js';

export interface SubtitleCorrection { from: string; to: string }

const bareWord = (text: string): string =>
  text.toLowerCase().replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '');

/** Use this for validation and rendering so a saved rule cannot silently miss. */
export function applySubtitleCorrections(words: Word[], corrections: SubtitleCorrection[]): { words: Word[]; matches: number[]; changes: number[] } {
  const matches = corrections.map(() => 0);
  const changes = corrections.map(() => 0);
  if (!corrections.length) return { words, matches, changes };
  const rules = corrections.map((correction, index) => ({
    index,
    from: correction.from.trim().split(/\s+/).map(bareWord).filter(Boolean),
    to: correction.to.trim(),
  })).filter(rule => rule.from.length > 0 && rule.to !== '')
    .sort((a, b) => b.from.length - a.from.length);
  const out: Word[] = [];
  let i = 0;
  while (i < words.length) {
    let applied = false;
    for (const rule of rules) {
      const n = rule.from.length;
      if (i + n > words.length || rule.from.some((part, k) => bareWord(words[i + k]!.text) !== part)) continue;
      const first = words[i]!;
      const last = words[i + n - 1]!;
      const lead = /^[^\p{L}\p{N}]*/u.exec(first.text)?.[0] ?? '';
      const trail = /[^\p{L}\p{N}]*$/u.exec(last.text)?.[0] ?? '';
      const startsUpper = /^\p{Lu}/u.test(first.text.slice(lead.length));
      const to = startsUpper && /^\p{Ll}/u.test(rule.to)
        ? rule.to[0]!.toUpperCase() + rule.to.slice(1) : rule.to;
      const replacement = `${lead}${to}${trail}`;
      out.push({ ...first, text: replacement, end: last.end });
      matches[rule.index]! += 1;
      if (replacement !== words.slice(i, i + n).map(word => word.text).join(' ')) changes[rule.index]! += 1;
      i += n;
      applied = true;
      break;
    }
    if (!applied) { out.push(words[i]!); i++; }
  }
  return { words: out, matches, changes };
}
